
import { FSharpRef, Record } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { class_type, record_type, bool_type, option_type, int32_type, list_type, string_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { materialiseTree, multiPathSheets, getSheetShapes, SheetTree, SheetTree_$reflection } from "../MenuHelpers.js";
import { cons, collect, contains as contains_1, tryHead, isEmpty, tryLast, ofArray, choose, map, empty } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { ofList, tryFind, empty as empty_1 } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { safeHash, equals, int32ToString, compare } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { split, join } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { tryParse } from "../../fable_modules/fable-library-js.5.13.0/Int32.js";
import { SimulatedDesign__InstancesInside_Z47F38896, FastSimulation__get_SimulatedCanvasState, FastSimulation__get_SimulatedTopSheet, SimulatedDesign__LabelPathOfInstance_1A26531 } from "../../Simulator/SimTypes.js";
import { memoizeByIdentity } from "../../Common/Helpers.js";
import { contains } from "../../fable_modules/fable-library-js.5.13.0/Set.js";
import { toArray, defaultArg } from "../../fable_modules/fable-library-js.5.13.0/Option.js";

/**
 * One node of the collapsed hierarchy, resolved against the simulation.
 * 
 * The field names are prefixed because F# resolves an unannotated record field to the last type
 * declaring it, and every file compiled after this one is full of trees, keys and instances.
 */
export class SelectorNode extends Record {
    constructor(NodeKey, NodeInstances, NodeInstance, NodeMultiRoute, NodeCollapsible) {
        super();
        this.NodeKey = NodeKey;
        this.NodeInstances = NodeInstances;
        this.NodeInstance = NodeInstance;
        this.NodeMultiRoute = NodeMultiRoute;
        this.NodeCollapsible = NodeCollapsible;
    }
}

export function SelectorNode_$reflection() {
    return record_type("WaveSimHierarchy.SelectorNode", [], SelectorNode, () => [["NodeKey", list_type(string_type)], ["NodeInstances", list_type(list_type(int32_type))], ["NodeInstance", option_type(list_type(int32_type))], ["NodeMultiRoute", bool_type], ["NodeCollapsible", bool_type]]);
}

/**
 * What the two panes of the wave selector draw, worked out together.
 */
export class SelectorHierarchy extends Record {
    constructor(HierTree, HierNodes, HierOrder) {
        super();
        this.HierTree = HierTree;
        this.HierNodes = HierNodes;
        this.HierOrder = HierOrder;
    }
}

export function SelectorHierarchy_$reflection() {
    return record_type("WaveSimHierarchy.SelectorHierarchy", [], SelectorHierarchy, () => [["HierTree", SheetTree_$reflection()], ["HierNodes", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [list_type(string_type), SelectorNode_$reflection()])], ["HierOrder", list_type(SelectorNode_$reflection())]]);
}

export const emptyHierarchy = new SelectorHierarchy(new SheetTree(empty(), "", empty(), empty(), "", 1, 0, empty(), undefined), empty_1({
    Compare: (x, y) => (compare(x, y) | 0),
}), empty());

/**
 * An instance path written as one string, for a place that can carry only one - the value behind
 * a DOM option. Ids, dot separated. NOT for showing anyone: see labelPathOf for that.
 */
export function pathKey(_arg) {
    return join(".", map(int32ToString, _arg));
}

/**
 * The inverse, for reading a DOM option's value back.
 */
export function pathOfKey(key) {
    if (key === "") {
        return empty();
    }
    else {
        return choose((part) => {
            let matchValue;
            let outArg = 0;
            matchValue = [tryParse(part, 511, false, 32, new FSharpRef(() => (outArg | 0), (v) => {
                outArg = (v | 0);
            })), outArg];
            if (matchValue[0]) {
                return matchValue[1];
            }
            else {
                return undefined;
            }
        }, ofArray(split(key, ["."], undefined, 0)));
    }
}

/**
 * An instance path as a person reads it - see `SimulatedDesign.LabelPathOfInstance`, which is
 * where this and the instance queries it is built from now live. Kept as a function here because
 * the selector calls it in several places and a `fs.Design.` prefix at each says nothing.
 */
export function labelPathOf(fs, instance) {
    return SimulatedDesign__LabelPathOfInstance_1A26531(fs.Design, instance);
}

function topInstance(fs) {
    if (FastSimulation__get_SimulatedTopSheet(fs) === "") {
        return undefined;
    }
    else {
        return empty();
    }
}

const simulatedShapes = memoizeByIdentity((fs) => {
    const shapes = getSheetShapes((_arg) => false, FastSimulation__get_SimulatedCanvasState(fs));
    return [shapes, multiPathSheets(shapes, FastSimulation__get_SimulatedTopSheet(fs))];
});

function hierarchyOf(wholeTree, fs, ws) {
    const patternInput = simulatedShapes(fs);
    const shapes = patternInput[0];
    const multiRoute = (key) => {
        const matchValue = tryLast(key);
        if (matchValue == null) {
            return false;
        }
        else {
            return contains(matchValue, patternInput[1]);
        }
    };
    const collapsible = (key_1) => {
        if (multiRoute(key_1)) {
            const matchValue_1 = tryLast(key_1);
            if (matchValue_1 == null) {
                return false;
            }
            else {
                return !isEmpty(defaultArg(tryFind(matchValue_1, shapes), empty()));
            }
        }
        else {
            return false;
        }
    };
    const tree = materialiseTree((key_2) => ((wholeTree ? true : !collapsible(key_2)) ? true : contains(key_2, ws.ShowSheetDetail)), false, shapes, FastSimulation__get_SimulatedTopSheet(fs));
    const walk = (node, instances) => {
        let chosen;
        const matchValue_2 = tryFind(node.SheetPath, ws.SelectedSheetInstance);
        let matchResult, recorded_1;
        if (matchValue_2 != null) {
            if (contains_1(matchValue_2, instances, {
                Equals: equals,
                GetHashCode: (x) => (safeHash(x) | 0),
            })) {
                matchResult = 0;
                recorded_1 = matchValue_2;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0: {
                chosen = recorded_1;
                break;
            }
            default:
                chosen = tryHead(instances);
        }
        return cons(new SelectorNode(node.SheetPath, instances, chosen, multiRoute(node.SheetPath), collapsible(node.SheetPath)), collect((sub) => {
            if (chosen == null) {
                return walk(sub, empty());
            }
            else {
                return walk(sub, SimulatedDesign__InstancesInside_Z47F38896(fs.Design, chosen, sub.SheetName));
            }
        }, node.SubSheets));
    };
    const order = walk(tree, ofArray(toArray(topInstance(fs))));
    return new SelectorHierarchy(tree, ofList(map((node_1) => [node_1.NodeKey, node_1], order), {
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    }), order);
}

/**
 * The hierarchy to draw: the sheets of the simulated design below its top sheet, collapsed so that
 * several instances of one sheet inside one parent are one node, cut off below any node the user
 * has not opened, and resolved against `fs` so each node names the instance it is showing.
 */
export function getSelectorHierarchy(fs, ws) {
    return hierarchyOf(false, fs, ws);
}

/**
 * The slice the selector takes through the design's instances: the instance each sheet is showing,
 * top sheet first, every sheet of the design and one entry per place in the tree it appears.
 * 
 * Which instance that is, is the user's choice wherever they have made one - the same
 * SelectedSheetInstance chain the pills resolve, validated the same way - and the alphabetically
 * first otherwise. A node the user has not opened is included: it is showing an instance like any
 * other, it is simply not drawn until they open it.
 * 
 * This is what "everything the selector is currently pointed at" means for a question asked of the
 * whole design rather than of one node, such as which Viewers to select when nothing has been
 * chosen. It is a fact about the SHEETS - one instance each - so it costs the design and not the
 * expansion.
 */
export function selectorInstances(fs, ws) {
    return choose((node) => node.NodeInstance, hierarchyOf(true, fs, ws).HierOrder);
}

/**
 * The node a SheetTree node is drawn as, for the pills - which are handed SheetTree nodes by the
 * breadcrumb renderer and need what was worked out about them here.
 */
export function nodeOf(hierarchy, sheet) {
    return tryFind(sheet.SheetPath, hierarchy.HierNodes);
}

